import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { IconSearch } from '@rossum/ui/icons/tabler';
import {
  Button,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@rossum/ui/material';

type FieldItem = {
  id: string;
  row: number;
};

type FieldFinderProps = {
  fields: FieldItem[];
  onFieldSelect: (field: FieldItem) => void;
  disabled?: boolean;
};

export const FieldFinder = ({
  fields,
  onFieldSelect,
  disabled,
}: FieldFinderProps) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [searchValue, setSearchValue] = useState('');

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setSearchValue('');
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (field: FieldItem) => {
    onFieldSelect(field);
    handleClose();
  };

  const filteredFields = useMemo(
    () =>
      fields.filter(field =>
        field.id.toLowerCase().includes(searchValue.toLowerCase())
      ),
    [fields, searchValue]
  );

  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        onClick={handleOpen}
        disabled={disabled}
        size="small"
        variant="text"
        startIcon={
          <SvgIcon fontSize="medium">
            <IconSearch />
          </SvgIcon>
        }
        data-cy="find-field-button"
        sx={{ textTransform: 'none', minWidth: 'auto' }}
      >
        {intl.formatMessage({ id: 'components.editor.tooltips.findField' })}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Stack sx={{ width: 300, maxHeight: 400 }}>
          <TextField
            autoFocus
            size="small"
            placeholder={intl.formatMessage({
              id: 'components.editor.fieldFinder.searchPlaceholder',
            })}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            sx={{ m: 1.5, mb: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon fontSize="medium">
                    <IconSearch />
                  </SvgIcon>
                </InputAdornment>
              ),
            }}
          />
          {filteredFields.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 2, textAlign: 'center' }}
            >
              {intl.formatMessage({
                id: 'components.editor.fieldFinder.noResults',
              })}
            </Typography>
          ) : (
            <List sx={{ overflow: 'auto', maxHeight: 300 }}>
              {filteredFields.map(field => (
                <ListItemButton
                  key={`${field.id}-${field.row}`}
                  onClick={() => handleSelect(field)}
                  dense
                >
                  <ListItemText
                    primary={field.id}
                    secondary={`Line ${field.row + 1}`}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Stack>
      </Popover>
    </>
  );
};
